import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Checkbox } from 'antd';

import GamePlayApis from '../../services/apis/gameplay';

import './style.scss';
import Toast from 'components/dumb/Toast';
import WebcamCapture from 'components/dumb/WebCam/WebCam';
import PermissionsModal from 'components/dumb/PermissionsModal';
import FullscreenModal from './FullscreenModal';
import { Note, Instructions, MicTest, ScreenShareTest } from '../../components/RulesScreen';
import { CompanyLogo } from 'components/common';
import { storage } from 'services/config/storage';
import { ASSESSMENT_SECTION_ORG_IDS, GODREJ_SECTION_IDS, PERMISSION_ERRORS, PERMISSION_DEVICE_TYPES, POST_WORKSHOP_GODREJ, PRE_WORKSHOP_GODREJ, APNA_ORG_SECTION_IDS } from 'constants/globalConstants';
import ProctoringApi from 'services/apis/proctoring';
import { useIsMobileDevice } from 'hooks';
import { useGameplayConfig } from 'store';
import { classifyMediaDeviceError, getPrivacyPolicyLink, isScreenProctoredEnabled } from 'utils/helper';
import AssessmentSections from 'components/RulesScreen/AssessmentSections';
import { GodrejSection } from 'components/RulesScreen/GodrejSection';
import { useTranslation } from 'react-i18next';
import analytics from 'services/config/analytics';

const RulesScreen = (props) => {
  /**
   * ===================
   * props from parent
   * ===================
   */

  /**
   * ===================
   * props from redux
   * ===================
   */

  /**
   * ===================
   * constant variables
   * ===================
   */
  const loginType = storage.get.loginType()

  const privacyPolicyLink = getPrivacyPolicyLink();

  const orgId = storage.get.orgId()
  const showAssessmentSection = ASSESSMENT_SECTION_ORG_IDS.includes(orgId);

  /**
   * ===================
   * useStates
   * ===================
   */
  const [apiInProgress, setApiInProgress] = useState(false);
  const [showMobileCamera, setShowMobileCamera] = useState(false);
  const [isMicTestVisible, setIsMicTestVisible] = useState(false)
  const [isScreenTestVisible, setIsScreenTestVisible] = useState(false)
  // UB-1056: Hardcoded until UI
  const [consent, setConsent] = useState(true);
  const [imgSrc, setImgSrc] = useState('');
  const [fullScreenPermissionVisible, setFullScreenPermissionVisible] = useState(false)
  const [isMediaPermissionChecked, setIsMediaPermissionChecked] = useState(false)
  const [mediaPermissionModalType, setMediaPermissionModalType] = useState("")
  const cameraSupportRef = useRef(false)

  /**
   * ===================
   * custom hooks
   * ===================
   */
  const {
    isVideoProctored,
    isProctored,
    isScreenProctored,
    isFullScreen,
    positionId,
    configLanguageSettings,
    instructionsContent,
  } = useGameplayConfig()
  const { isRtlLayout, layoutDirection } = configLanguageSettings;
  const { t } = useTranslation();
  const isMobile = useIsMobileDevice();
  const history = useHistory();
  let { id: linkId } = useParams();
  const checkScreenShare = useMemo(() => isScreenProctoredEnabled({ isScreenProctored, isMobile }), [isScreenProctored, isMobile])

  const showGameplayOnly = storage.get.showGameplayOnly();


  /**
   * ===================
   * useEffects
   * ===================
   */
  useEffect(() => {
    window.scrollTo(0, 0);
    analytics.track('rules_screen_viewed');
  }, []);

  useEffect(() => {
    (async () => {
      // Sdk changes for allowing proctoring during linear gameplays
      const showGameplayAndNotProctored = showGameplayOnly && !(isVideoProctored || isProctored);
      if (showGameplayAndNotProctored) {
        startGame();
        return
      }
      if (isVideoProctored || isProctored) {
        let failureReason = ""
        try {
          const mediaDevices = await navigator.mediaDevices.enumerateDevices()
          const videoDevices = mediaDevices.filter(({ kind, label }) => kind === 'videoinput' && !label.toLowerCase().includes('back') && !label.toLowerCase().includes('obs'))
          const audioDevices = mediaDevices.filter(({ kind }) => kind === 'audioinput')
          if (isVideoProctored) {
            if (audioDevices.length == 0 || videoDevices.length == 0) {
              failureReason = PERMISSION_ERRORS.NO_DEVICE
            } else {
              const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
              if (stream) failureReason = ""
            }
          } else if (isProctored) {
            if (videoDevices.length == 0) {
              failureReason = PERMISSION_ERRORS.NO_DEVICE
            } else {
              const stream = await navigator.mediaDevices.getUserMedia({ video: true })
              if (stream) failureReason = ""
            }
          }
        } catch (error) {
          failureReason = classifyMediaDeviceError(error?.toString())
        } finally {
          setMediaPermissionModalType(failureReason)
          setIsMediaPermissionChecked(true)
        }
      } else if (checkScreenShare) {
        setIsScreenTestVisible(true)
      }
    })()
  }, [isVideoProctored, isProctored, checkScreenShare])

  /**
   * ===================
   * useRefs
   * ===================
   */

  /**
   * ===================
   * computed variables
   * ===================
   */
  const hasGodrejPosition = GODREJ_SECTION_IDS.includes(positionId);
  const hasApnaOrgPosition = APNA_ORG_SECTION_IDS.includes(positionId);
  const isPostWorkshopPosition = POST_WORKSHOP_GODREJ.includes(positionId)
  const isPreWorkshopPosition = PRE_WORKSHOP_GODREJ.includes(positionId)

  /**
   * ===================
   * functions
   * ===================
   */
  const startGame = async ({ cameraSupport = null } = {}) => {
    analytics.track('rules_accepted');
    try {
      setApiInProgress(true);
      console.log(`[Updaing the game status] for link: `, linkId);
      // UB-1056: Hardcoded until UI
      if (!consent && !showGameplayOnly)
        throw new Error(
          'Consent required (see privacy policy) to start assessment.'
        );
      const res = await GamePlayApis.updateGamePlayStatus({ consent, cameraSupport, linkId }); // update gameplay status to playing
      if (res?.lastProctoredData?.createdAt) {
        let currentLatestSnapshot = storage.get.latestSnapshot(linkId)
        let savedLatestSnapshot = res.lastProctoredData.createdAt
        if (currentLatestSnapshot) {
          currentLatestSnapshot = new Date(currentLatestSnapshot)
          savedLatestSnapshot = new Date(savedLatestSnapshot)
          if (savedLatestSnapshot > currentLatestSnapshot) {
            storage.set.latestSnapshot(savedLatestSnapshot, linkId)
          }
        } else {
          storage.set.latestSnapshot(savedLatestSnapshot, linkId)
        }
      }
      console.log(`[Update game status] success`, linkId);
      setApiInProgress(false);
      if (isFullScreen && !isMobile) {
        setFullScreenPermissionVisible(true)
      } else {
        storage.set.linkId(linkId)
        linkId ? history.push(`/games/${linkId}`) : history.push(`/games`)
      }
    } catch (error) {
      analytics.track('proctoring_setup_failed', { error: error?.message || String(error) });
      Toast('', error?.message || error, 'We are sorry!');
      console.log(error);
    } finally {
      setApiInProgress(false)
    }
  };

  async function confirmUserPhoto({ cameraSupport }) {
    try {
      setApiInProgress(true)
      cameraSupportRef.current = cameraSupport
      if (isVideoProctored) {
        const sessionId = storage.get.userVideoSessionId()
        const response = await ProctoringApi.uploadVideoProctoringImage(sessionId, imgSrc)
        if (response.success) {
          setIsMicTestVisible(true)
        }
      } else if (cameraSupport) {
        storage.set.latestSnapshot((new Date()).toISOString(), linkId)
        const userRefImg = !!storage.get.userRefImg()
        if (!userRefImg) {
          storage.set.userRefImg(imgSrc)
          await ProctoringApi.sendUserSnapShot({
            linkId,
            imgSrc,
            isImageCritical: false,
            criticalReason: '',
          });
        }
        if (checkScreenShare) {
          setIsScreenTestVisible(true)
        } else {
          startGame({ cameraSupport })
        }
      }
    } catch (err) {
      console.error(err)
    } finally {
      setApiInProgress(false)
    }
  }

  const mobileContinueHandler = () => {
    if (isProctored || isVideoProctored) {
      setShowMobileCamera(true);
    } else if (!consent) {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    } else {
      startGame();
    }
  };

  const handleMicTestSuccess = () => {
    if (checkScreenShare) {
      setIsScreenTestVisible(true)
      setIsMicTestVisible(false)
    } else {
      startGame()
    }
  }

  const handleScreenTestSuccess = () => {
    startGame({ cameraSupport: cameraSupportRef.current })
  }

  /**
   * ===================
   * useCallback
   * ===================
   */

  /**
   * ===================
   * Comments for reference
   * ===================
   * 
   * show CompanyLogo only if not loaded from sdk (check showGameplayOnly)
   */
  return isMobile ? (
    <section className="rules-container mobile-layout h-full-page color-primary bg-color-primary flex" dir={layoutDirection}>
      {!showGameplayOnly && (
        <div className="mobile-sticky-header">
          <CompanyLogo />
        </div>
      )}
      <div className="h-full-fix width-p-50 flex flex-col justify-center items-start p-30">
        {showMobileCamera ? (
          <div className="flex flex-col items-center text-center w-full">
            {
              isMicTestVisible
                ? <p
                  className="color-primary weight-300 size-30 leading-36 mt-20 mb-20"
                >
                  {t("mic_test.last_step")}
                </p>
                : <>
                  <p className="color-primary weight-300 size-30 leading-36 mt-20 mb-20 webcam-section-title">
                    {t("webcam.verify_identity")}
                  </p>
                  <p className="color-primary font-medium opacity-70 size-14 webcam-section-subtitle">
                    {t("webcam.face_in_frame")}
                  </p>
                </>
            }
            {(isProctored || isVideoProctored || checkScreenShare) && (
              <div className="flex flex-col justify-center items-center mt-10 w-full">
                {
                  isScreenTestVisible
                    ? <ScreenShareTest
                      handleScreenTestSuccess={handleScreenTestSuccess}
                      privacyPolicyLink={privacyPolicyLink}
                      consent={consent}
                      setConsent={setConsent}
                    /> : isMicTestVisible
                      ? <MicTest onSuccess={handleMicTestSuccess} loading={apiInProgress} />
                      : isMediaPermissionChecked ? <WebcamCapture
                        initialCapture={true}
                        isMobile={isMobile}
                        imgSrc={imgSrc}
                        setImgSrc={setImgSrc}
                        confirmPhoto={confirmUserPhoto}
                        loading={apiInProgress}
                        consent={consent}
                        setConsent={setConsent}
                        isVideoProctored={isVideoProctored}
                      /> : <></>
                }
              </div>
            )}
          </div>
        ) : (
          <>
            {hasGodrejPosition && <GodrejSection isPostWorkshopPosition={isPostWorkshopPosition} isPreWorkshopPosition={isPreWorkshopPosition} />}
            <p className={`color-primary weight-300 mb-10 mt-20 ${hasGodrejPosition ? "size-24" : "size-24 leading-36"}`}>
              {
                !!instructionsContent.heading
                  ? instructionsContent.heading
                  : t("instructions.title")
              }
            </p>
            {
              !!instructionsContent.proctoring
                ? <div
                  className="note-div p-16 br-10 mt-20 mb-20 proctoring-notes-container"
                >
                  <h3 className="heading">{t('note.important_note')}</h3>
                  <div dangerouslySetInnerHTML={{ __html: instructionsContent.proctoring }} />
                </div>
                : <Note
                  visible={isProctored || isVideoProctored}
                  hasApnaOrgPosition={hasApnaOrgPosition}
                />
            }
            {showAssessmentSection && <AssessmentSections />}
            <div className="flex flex-col items-start mr-15">
              {
                !!instructionsContent.content
                  ? <div
                    dangerouslySetInnerHTML={{ __html: instructionsContent.content }}
                    className="position-instructions-container"
                  />
                  : <Instructions
                    loginType={loginType}
                    showAssessmentSection={showAssessmentSection}
                    hasGodrejPosition={hasGodrejPosition}
                    hasApnaOrgPosition={hasApnaOrgPosition}
                  />
              }
              {!(isProctored || isVideoProctored) && (
                <div className="flex items-center mb-90">
                  <Checkbox
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mr-10 bottom-10"
                  />
                  <p className="size-18 color-secondary m-2">
                    {t("webcam.agree_to_privacy_policy")}
                    <a
                      className="size-18 font-bold color-primary"
                      href={privacyPolicyLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {' '}
                      {t("webcam.privacy_policy")}
                    </a>
                    .
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {!showMobileCamera && (
        <div className="mobile-start-button">
          <Button
            type="primary"
            className="weight-600 size-16 game-start-btn"
            loading={apiInProgress}
            onClick={mobileContinueHandler}
          >
            {t("webcam.continue")}
          </Button>
        </div>
      )}
      <FullscreenModal
        fullScreenPermissionVisible={fullScreenPermissionVisible}
        setFullScreenPermissionVisible={setFullScreenPermissionVisible}
        isMobile={isMobile}
        linkId={linkId}
        history={history}
      />
      <PermissionsModal
        visible={!!mediaPermissionModalType}
        type={mediaPermissionModalType}
        device={isVideoProctored ? PERMISSION_DEVICE_TYPES.CAMERA_MIC : PERMISSION_DEVICE_TYPES.CAMERA}
      />
    </section>
  ) : (isProctored || isVideoProctored || checkScreenShare) ? (
    <section className="rules-container height-100 overflowX-hidden color-primary bg-color-primary flex pl-50 pr-50" dir={layoutDirection}>
      <div className="h-full-fix width-p-50 flex flex-col justify-start items-start p-30">
        {!showGameplayOnly && <CompanyLogo />}
        <div className={`flex flex-col justify-center items-start mt-40 left-section`}>
          {hasGodrejPosition && <GodrejSection isPostWorkshopPosition={isPostWorkshopPosition} isPreWorkshopPosition={isPreWorkshopPosition} />}
          <p className={`color-primary weight-500 mb-0 ${hasGodrejPosition ? "size-32 line-height-32" : "line-height-40 size-40"}`}>
            {
              !!instructionsContent.heading
                ? instructionsContent.heading
                : t("instructions.title")
            }
          </p>
          {
            !!instructionsContent.proctoring
              ? <div
                className="note-div p-16 br-10 mt-20 mb-20 proctoring-notes-container"
              >
                <h3 className="heading">{t('note.important_note')}</h3>
                <div dangerouslySetInnerHTML={{ __html: instructionsContent.proctoring }} />
              </div>
              : <Note
                visible={isProctored || isVideoProctored}
                hasApnaOrgPosition={hasApnaOrgPosition}
              />
          }
          {showAssessmentSection && <AssessmentSections />}
          {
            !!instructionsContent.content
              ? <div dangerouslySetInnerHTML={{ __html: instructionsContent.content }} className="position-instructions-container" />
              : <Instructions
                loginType={loginType}
                showAssessmentSection={showAssessmentSection}
                hasGodrejPosition={hasGodrejPosition}
              />
          }
        </div>
      </div>
      <div className={`h-full-fix sticky top-0 flex flex-col justify-center items-center flex-1`}>
        {
          isScreenTestVisible
            ? <ScreenShareTest
              handleScreenTestSuccess={handleScreenTestSuccess}
              privacyPolicyLink={privacyPolicyLink}
              consent={consent}
              setConsent={setConsent}
            /> : isMicTestVisible
              ? <MicTest onSuccess={handleMicTestSuccess} loading={apiInProgress} />
              : isMediaPermissionChecked ? <WebcamCapture
                initialCapture={true}
                imgSrc={imgSrc}
                setImgSrc={setImgSrc}
                confirmPhoto={confirmUserPhoto}
                loading={apiInProgress}
                consent={consent}
                setConsent={setConsent}
                isVideoProctored={isVideoProctored}
              /> : <></>
        }
      </div>
      <FullscreenModal
        fullScreenPermissionVisible={fullScreenPermissionVisible}
        setFullScreenPermissionVisible={setFullScreenPermissionVisible}
        isMobile={isMobile}
        linkId={linkId}
        history={history}
      />
      <PermissionsModal
        visible={!!mediaPermissionModalType}
        type={mediaPermissionModalType}
        device={isVideoProctored ? PERMISSION_DEVICE_TYPES.CAMERA_MIC : PERMISSION_DEVICE_TYPES.CAMERA}
      />
    </section>
  ) : (
    <section className="rules-container h-full-page color-primary bg-color-primary flex pl-50 pr-50" dir={layoutDirection}>
      <div className="sticky top-0 h-full-fix width-p-50 flex flex-col justify-center items-start p-30">
        {!showGameplayOnly && <CompanyLogo />}
        <p className="greeting color-primary font-bold size-40">
          {t("welcome_aboard.welcome") + t("welcome_aboard.aboard")}
        </p>
        <p className="color-primary font-light opacity-70 sub-content size-16">
          {t("welcome_aboard.subtext")}
        </p>
        <div className="mt-80">
          <div className="flex items-center">
            <Checkbox
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mr-10 relative bottom-10"
            />
            <p className="size-18 color-secondary">
              {t("webcam.agree_to_privacy_policy")}
              <a
                className="size-18 font-bold color-primary"
                href={privacyPolicyLink}
                target="_blank"
              >
                {' '}
                {t("webcam.privacy_policy")}
              </a>
              .
            </p>
          </div>
          <div className="desktop-start-button">
            <Button
              type="primary"
              className="font-medium game-start-btn"
              loading={apiInProgress}
              onClick={startGame}
              disabled={!consent}
            >
              {t("lets_go")}
            </Button>
          </div>
        </div>
      </div>
      <div className={`width-p-50 flex flex-col items-start p-30 justify-center`}>
        <div className="flex flex-col justify-start items-start p-30">
          {showAssessmentSection && <AssessmentSections />}
          <p className={`color-primary weight-500 mb-10 line-height-40 size-40`}>
            {
              !!instructionsContent.heading
                ? instructionsContent.heading
                : t("instructions.title")
            }
          </p>
          {
            !!instructionsContent.content
              ? <div dangerouslySetInnerHTML={{ __html: instructionsContent.content }} className="position-instructions-container" />
              : <Instructions
                loginType={loginType}
                showAssessmentSection={showAssessmentSection}
                hasGodrejPosition={hasGodrejPosition}
              />
          }
        </div>
      </div>
      <FullscreenModal
        fullScreenPermissionVisible={fullScreenPermissionVisible}
        setFullScreenPermissionVisible={setFullScreenPermissionVisible}
        isMobile={isMobile}
        linkId={linkId}
        history={history}
      />
    </section>
  );
};

export default RulesScreen;
